import useModalTrigger from 'magik-react-hooks/useModalTrigger'
import { Link } from 'react-router-dom'
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'
import Header from '../../components/Header'
import Page from '../../components/Page'
import Paginator from '../../components/Paginator'
import Sorter, { SortControl } from '../../components/Sorter'
import StickyTable from '../../components/StickyTable'
import { useQsFilters } from '../../hooks/filters'
import { Fonte } from '../../types'
import { Field, Formik } from 'formik'
import { MultiSelectField, ReactSelectField } from '../../components/form'
import { useDeleteFonte, useFonti, useUnificaFonti } from '../../hooks/fonti'

const initFilters = (params: URLSearchParams) => ({
  page: Number(params.get('page') ?? 1),
  search: params.get('search') ?? '',
  ordering: params.get('ordering') ?? '',
})

export default function FontiList() {
  const { filters, uiFilters, setFiltersDebounced, setFilters } =
    useQsFilters(initFilters)
  const { data } = useFonti(filters)
  const deleteFonte = useDeleteFonte()
  const [modalRm, modalRmActions] = useModalTrigger<Fonte>()
  const [modalUnifica, modalUnificaActions] = useModalTrigger()

  const filtersRegisti = {
    page_size: 1000,
  }

  const { data: fontiFull } = useFonti(filtersRegisti)

  const fontiOptions =
    fontiFull?.results.map((fonte) => ({
      value: fonte.id,
      label: fonte.nome,
    })) ?? []

  const unificaFonti = useUnificaFonti()

  return (
    <Page padded>
      <Header>
        <div className="d-flex align-items-end">
          <h2 className="m-0">Fonti</h2>
          <div className="counter ms-2 mb-1">
            <small>
              {data!.count}
              {' / '}
              {data!.total_count}
            </small>
          </div>
        </div>
        <div className="d-flex">
          <Button
            color="primary"
            className="me-2"
            outline
            onClick={modalUnificaActions.toggle}
          >
            Unifica
          </Button>
          <Link className="btn btn-primary" to="/fonti/new">
            <i className="bi bi-plus-circle me-2"></i> Aggiungi
          </Link>
        </div>
      </Header>
      <div className="d-flex justify-content-between mt-3 mb-3 align-items-center">
        <input
          placeholder="Cerca"
          className="form-control w-auto"
          type="text"
          value={uiFilters.search}
          onChange={(e) =>
            setFiltersDebounced({ search: e.target.value, page: 1 })
          }
        />
      </div>
      <StickyTable className="flex-1">
        <thead>
          <Sorter
            value={uiFilters.ordering}
            onSortChange={(ordering) =>
              setFilters({
                ordering,
                page: 1,
              })
            }
          >
            <tr>
              <th>#</th>
              <th>
                <div className="d-flex align-items-center">
                  Nome
                  <SortControl field="nome" />
                </div>
              </th>
            </tr>
          </Sorter>
        </thead>
        <tbody>
          {data!.results.map((fonte) => (
            <tr key={fonte.id}>
              <td>{fonte.id}</td>
              <td>
                <Link to={`/fonti/${fonte.id}`}>{fonte.nome}</Link>
              </td>
            </tr>
          ))}
        </tbody>
      </StickyTable>
      <Modal centered isOpen={modalRm.isOpen} toggle={modalRmActions.toggle}>
        <ModalHeader toggle={modalRmActions.toggle}>Elimina</ModalHeader>
        <ModalBody>
          {modalRm.value && (
            <div className="pt-3 pb-3">
              Sei sicuro di voler eliminare la fonte
              {modalRm.value.nome} ?
            </div>
          )}
        </ModalBody>
        <ModalFooter className="d-flex justify-content-between">
          <button
            onClick={modalRmActions.toggle}
            className="btn btn-outline-primary"
          >
            Annulla
          </button>
          <button
            className="btn  btn-danger"
            disabled={deleteFonte.isLoading}
            onClick={async () => {
              await deleteFonte.mutateAsync(modalRm.value!.id)
              modalRmActions.close()
            }}
          >
            <i className="bi bi-trash3-fill"></i> Elimina
          </button>
        </ModalFooter>
      </Modal>
      <Modal
        centered
        isOpen={modalUnifica.isOpen}
        toggle={modalUnificaActions.toggle}
      >
        <ModalHeader toggle={modalUnificaActions.toggle}>Unifica</ModalHeader>
        <Formik
          initialValues={{
            fonte_to_keep: null,
            fonti_to_delete: [],
          }}
          onSubmit={async (values) => {
            if (
              !values.fonte_to_keep ||
              values.fonti_to_delete.length === 0
            )
              return
            await unificaFonti
              .mutateAsync({
                fonte_to_keep: values.fonte_to_keep,
                fonti_to_delete: values.fonti_to_delete,
              })
              .then(() => {
                modalUnificaActions.toggle()
              })
          }}
        >
          {({ values, handleChange, handleSubmit }) => (
            <form onSubmit={handleSubmit}>
              <ModalBody>
                <div>
                  <Field
                    component={ReactSelectField}
                    name="fonte_to_keep"
                    label="Fonte da mantenere"
                    placeholder="Seleziona la fonte da mantenere"
                    options={fontiOptions}
                  />
                </div>
                <div>
                  <Field
                    component={MultiSelectField}
                    name="fonti_to_delete"
                    placeholder="Seleziona le fonti da eliminare"
                    label="Fonti da eliminare"
                    options={fontiOptions.filter(
                      (option) => option.value !== values.fonte_to_keep
                    )}
                    isMulti
                  />
                </div>
              </ModalBody>
              <ModalFooter className="d-flex justify-content-between">
                <button
                  onClick={modalUnificaActions.toggle}
                  className="btn btn-outline-primary"
                >
                  Annulla
                </button>
                <button className="btn btn-success" type="submit">
                  <i className="bi bi-save-fill me-1"></i>
                  Salva
                </button>
              </ModalFooter>
            </form>
          )}
        </Formik>
      </Modal>
      <div className="position-relative">
        <Paginator
          count={data!.count}
          currentPage={uiFilters.page}
          goToPage={(page) => setFilters({ page })}
        />
      </div>
    </Page>
  )
}
